import { createContext, useContext, useState, useMemo } from 'react';
import type { ReactNode } from 'react';
import {
	collection,
	doc,
	getDocs,
	addDoc,
	updateDoc,
	query,
	where,
	deleteField
} from 'firebase/firestore';
import {
	createObjectStore,
	deleteDB,
	deleteValue,
	getAllValues,
	putBulkValues,
	putValue
} from '../data/indexedDBHelper';
import { isPullExpired } from '../data/utils';
import { db } from '../gcp/config';
import useQuestionBankContext from './QuestionBankContext';
import useUserContext from './UserContext';
import {
	buildQuestionObject,
	buildTestRecordObject,
	buildUserObject
} from './xBuildObjectUtils';
import type {
	CreateQuestionData,
	Question,
	TestRecord
} from '../data/interface-question';
import type { QuestionBankID } from '../data/interface-question-bank';
import type { User } from '../data/interface-user';
import type { FirebaseError } from 'firebase/app';
import type { IDBPDatabase } from 'idb';

export type AdminContextType = {
	getUserList: (
		productID: QuestionBankID,
		targetYear: number
	) => Promise<User[]>;
	users: User[] | undefined;
	setUsers: (users: User[]) => void;
	// GET QUESTIONS
	createQuestion: (
		question: CreateQuestionData
	) => Promise<Question[] | FirebaseError | null>;
	adminQuestions: Question[] | undefined;
	setAdminQuestions: (v: Question[] | undefined) => void;
	getQuestionsForAdmin: (id: QuestionBankID) => Promise<Question[]>;
	pruebasDiagnosticas: TestRecord[];
	getRecordsPruebasDiagnosticas: () => Promise<TestRecord[]>;
	updateQuestionbyKey: (
		questionID: string,
		key: string,
		newValue: any
	) => Promise<Question | null>;
};
export const AdminContext = createContext<AdminContextType | null>(null);
export const AdminContextProvider = ({ children }: { children: ReactNode }) => {
	const { getQuestion } = useQuestionBankContext();
	const { currentUser } = useUserContext();
	const [adminQuestions, setAdminQuestions] = useState<
		Question[] | undefined
	>();
	const [pruebasDiagnosticas, setPruebasDiagnosticas] = useState<TestRecord[]>(
		[]
	);

	const validateAdmin = () => {
		if (currentUser === null) return false;
		// TODO: Redirect to login page / HANDLE THIS CASE HERE
		// navigateTo('/login');
		return currentUser.isAdmin ?? false;
	};

	// #region IDBP HELPERS
	/**
	 * Opens an IndexedDB connection
	 */
	const openDBConnection = async (
		dbName = 'prepmed-questions',
		tableNamesArray = ['questions']
	) => {
		// If there's already a connection in progress, wait for it
		if (isConnecting && connectionPromise !== null) {
			return await connectionPromise;
		}

		// Set connection lock
		isConnecting = true;
		connectionPromise = createObjectStore(dbName, 200, tableNamesArray);

		try {
			const db = await connectionPromise;
			return db;
		} catch (error) {
			console.error('Error opening IndexedDB connection:', error);
			throw error;
		} finally {
			isConnecting = false;
			connectionPromise = null;
		}
	};
	// #endregion

	// #region USERS
	const [users, setUsers] = useState<User[]>();
	const getUserList = async (productID: QuestionBankID, targetYear: number) => {
		const isAdmin = validateAdmin();
		if (!isAdmin) return [];
		// if (users !== undefined && users?.length > 0) return users;
		const userList = await getUsersFromFirestore(productID, targetYear);
		return userList;
	};

	const getUsersFromFirestore = async (
		productID: QuestionBankID,
		targetYear: number
	): Promise<User[]> => {
		const userList: User[] = [];
		const usersCollection = 'users';

		const recordsRef = collection(db, usersCollection);
		const q = query(
			recordsRef,
			where('examTarget', '==', productID),
			where('examTargetYear', '==', targetYear)
		);

		const querySnapshot = await getDocs(q);
		querySnapshot?.forEach((record) => {
			const r = buildUserObject(record.id, record.data());
			if (r !== null) userList.push(r);
		});

		setUsers(userList);
		return userList;
	};
	// #endregion

	// #region QUESTIONS
	let isConnecting = false;
	let connectionPromise: Promise<IDBPDatabase | undefined> | null = null;
	const getQuestionsForAdmin = async (bankID: QuestionBankID) => {
		let data: Question[] = [];
		if (currentUser === null) return data;
		const isAdmin = validateAdmin();
		if (!isAdmin) return [];

		let thisDB: IDBPDatabase | undefined;

		try {
			// Clear local storage and delete DB if needed
			if (isPullExpired(isAdmin)) {
				localStorage.removeItem('prepmed-last-questions-pull-date');
				// Close any existing connections before deleting
				if (isConnecting && connectionPromise !== null) {
					const existingDB = await connectionPromise;
					if (existingDB !== undefined) {
						existingDB.close();
					}
				}
				await deleteDB('prepmed-questions');
			}

			// Open new connection
			thisDB = await openDBConnection();

			if (thisDB !== undefined) {
				data = await getAllValues(thisDB, 'questions');
			}
			const hasLocalData = data.length > 0;

			if (!hasLocalData) {
				if (adminQuestions !== undefined && adminQuestions.length > 0) {
					// GET QUESTIONS FROM USESTATE
					data = adminQuestions;
				} else {
					// GET QUESTIONS FROM FIRESTORE
					data = await getQuestionsFromFirestore(bankID);
				}
			}

			// Check if the data is from the correct bank
			if (
				hasLocalData ||
				(adminQuestions !== undefined && adminQuestions.length > 0)
			) {
				const isFromBank = data.every((q) => q.bank === bankID);
				if (!isFromBank) {
					data = await getQuestionsFromFirestore(bankID);
				}
			}

			// Update state and IndexedDB
			setAdminQuestions(data);
			if (thisDB !== undefined && data.length > 0) {
				await putBulkValues(thisDB, 'questions', data);
			}
		} catch (error) {
			console.error('Error in getQuestionsForAdmin:', error);
			// If there's an error, try to get questions directly from Firestore
			data = await getQuestionsFromFirestore(bankID);
			setAdminQuestions(data);
		} finally {
			// Always close the database connection
			if (thisDB !== undefined) {
				thisDB.close();
			}
		}
		return data;
	};

	const getQuestionsFromFirestore = async (
		id: QuestionBankID
	): Promise<Question[]> => {
		const questionList: Question[] = [];
		const questionsCollection = 'questions';

		const recordsRef = collection(db, questionsCollection);
		const q = query(recordsRef, where('bank', '==', id));

		const querySnapshot = await getDocs(q);
		querySnapshot?.forEach((record) => {
			const r = buildQuestionObject(record.id, record.data());
			questionList.push(r as Question);
		});

		setAdminQuestions(questionList);
		return questionList;
	};

	const createQuestion = async (
		question: CreateQuestionData
	): Promise<Question[] | FirebaseError | null> => {
		const isAdmin = validateAdmin();
		if (!isAdmin) return null;
		const questionsCollection = 'questions';
		const recordsRef = collection(db, questionsCollection);

		const fullQuestion = {
			...question,
			createdOn: new Date(),
			authorID: currentUser?.uid,
			authorFirstName: currentUser?.person?.firstName,
			authorLastName: currentUser?.person?.lastName
		};

		const result = addDoc(recordsRef, fullQuestion)
			.then(async (docRef) => {
				const questions: Question[] = [];
				const q = await getQuestion(docRef.id);
				if (q !== null) {
					questions.push(q);
				}

				if (adminQuestions !== undefined) {
					questions.push(...adminQuestions);
				}
				setAdminQuestions(questions);
				return questions;
			})
			.catch((error: FirebaseError) => {
				// TODO: Handle error
				console.error('Error creating question:', error);
				return error;
			});
		return await result;
	};

	/**
	 * Updates a question field, takes 3 params: questionID, fieldKey & newValue
	 */
	const updateQuestionbyKey = async (
		questionID: string,
		key: string,
		newValue: any
	): Promise<Question | null> => {
		// UPDATE USER RECORDS
		const editor = currentUser === null ? '' : currentUser.uid;
		const questionRef = doc(db, 'questions', questionID);
		if (key === 'issues' && newValue === undefined) {
			await updateDoc(questionRef, {
				issues: deleteField(),
				lastEditDate: new Date(),
				lastEditBy: editor
			});
		} else if (key === 'imageFileName' && newValue === undefined) {
			await updateDoc(questionRef, {
				imageFileName: deleteField(),
				lastEditDate: new Date(),
				lastEditBy: editor
			});
		} else if (key === 'audioFileName' && newValue === undefined) {
			await updateDoc(questionRef, {
				audioFileName: deleteField(),
				lastEditDate: new Date(),
				lastEditBy: editor
			});
		} else {
			await updateDoc(questionRef, {
				[key]: newValue,
				lastEditDate: new Date(),
				lastEditBy: editor
			});
		}

		const q = await getQuestion(questionID);
		if (q !== null) {
			// Update the local state if the question is found
			setAdminQuestions((prev) => {
				if (prev === undefined) return [q];
				const index = prev.findIndex((item) => item.id === questionID);
				if (index !== -1) {
					const updatedQuestions = [...prev];
					updatedQuestions[index] = q; // Update the specific question
					return updatedQuestions;
				}
				return [...prev, q]; // If not found, add it to the list
			});

			// Update IndexedDB
			let thisDB: IDBPDatabase | undefined;
			try {
				// Set connection lock
				isConnecting = true;
				connectionPromise = openDBConnection();
				thisDB = await connectionPromise;
				if (thisDB !== undefined) {
					// First delete the existing record
					await deleteValue(thisDB, 'questions', questionID);
					// Then add the updated record
					await putValue(thisDB, 'questions', q);
				}
			} catch (error) {
				console.error('Failed to update IndexedDB:', error);
				// Continue execution even if IndexedDB update fails
			} finally {
				if (thisDB !== undefined) {
					thisDB.close();
				}
				isConnecting = false;
				connectionPromise = null;
			}
		}
		return q;
	};
	// #endregion

	// #region REPORTS
	const getRecordsPruebasDiagnosticas = async (): Promise<TestRecord[]> => {
		const records: TestRecord[] = [];
		const recordsCollection = 'testRecords';

		const recordsRef = collection(db, recordsCollection);
		const q = query(recordsRef, where('testLabel', '==', 'evaluacion-1'));
		const q2 = query(recordsRef, where('testLabel', '==', 'evaluacion-2'));

		try {
			const querySnapshot = await getDocs(q);
			querySnapshot?.forEach((record) => {
				const data = buildTestRecordObject(record.id, record.data());
				if (data !== null) {
					setPruebasDiagnosticas((prev) => [...prev, data]);
					records.push(data);
				}
			});

			const querySnapshot2 = await getDocs(q2);
			querySnapshot2?.forEach((record) => {
				const data = buildTestRecordObject(record.id, record.data());
				if (data !== null) {
					setPruebasDiagnosticas((prev) => [...prev, data]);
					records.push(data);
				}
			});
		} catch (error) {
			console.error('Error fetching records for Pruebas Diagnosticas:', error);
		}

		return records;
	};
	// #endregion

	return (
		<AdminContext.Provider
			value={useMemo(
				() => ({
					getUserList,
					users,
					setUsers,
					createQuestion,
					adminQuestions,
					setAdminQuestions,
					getQuestionsForAdmin,
					pruebasDiagnosticas,
					getRecordsPruebasDiagnosticas,
					updateQuestionbyKey
				}),
				[users, adminQuestions, pruebasDiagnosticas, currentUser]
			)}>
			{children}
		</AdminContext.Provider>
	);
};
export const useAdminContext = () => {
	const context = useContext(AdminContext);
	if (context === undefined || context === null) {
		throw new Error(
			'useAdminContext must be used within a AdminContextProvider'
		);
	}
	return context;
};
export default AdminContextProvider;
